import React, { useState, useEffect } from 'react';
import { Accordion, Table, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  inspectionResultsDataFilters,
  selectFilteredInspectionResultss,
  selectInspectionResultsLoading,
} from './store/selectors';
import { showModal } from 'Core/redux/main/slice';
import { fetchInspectionResultsData } from './store/slice';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { actions as inspectionResultsActions } from './store/slice';
import { Button, ButtonGroup, Form, Card } from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Info, Download, FileText } from 'react-feather';
import { ZoomableImageModal } from 'Core/components';
import { downloadImage } from 'Core/utils';
import { findFocusedAqlIndex } from './inspection-results-deep-link';

// Custom styles for AQL Results Accordion
const accordionStyles = {
  aqlResultsAccordion: {
    '& .accordion-header': {
      backgroundColor: '#f8f9fa',
    },
    '& .accordion-button': {
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #dee2e6',
      '&:not(.collapsed)': {
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
      },
    },
    '& .accordion-body': {
      backgroundColor: '#fafafa',
    },
  },
  fileItem: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '4px 8px',
    backgroundColor: '#f5f5f5',
  },
};

const { setSelectedPart, setInspectionResultsDataFilters } = inspectionResultsActions;

const { INSPECTIONRESULTS_ADD_EDIT } = MODAL_TYPES;

const InspectionAQLResults = ({
  data,
  selectedPart,
  pagination,
  onPageChange,
  focusedResultId,
  focusedWorkTicket,
}) => {
  const dispatch = useDispatch();
  const inspectionResultsDataFilter = useSelector(inspectionResultsDataFilters);
  const [fromDate, setFromDate] = useState(inspectionResultsDataFilter.fromDate);
  const [toDate, setToDate] = useState(inspectionResultsDataFilter.toDate);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const { filteredData, isLoading } = useSelector(state => ({
    filteredData: selectFilteredInspectionResultss(state),
    isLoading: selectInspectionResultsLoading(state),
  }));

  // State for zoomable image modal
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeKey, setActiveKey] = useState('0');

  useEffect(() => {
    const focusedIndex = findFocusedAqlIndex(data, focusedResultId, focusedWorkTicket);
    if (focusedIndex >= 0) {
      setActiveKey(String(focusedIndex));
      const resultId = data[focusedIndex]?.id;
      if (resultId == null) {
        return;
      }
      const frame = requestAnimationFrame(() => {
        document
          .getElementById(`aql-result-${resultId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      return () => cancelAnimationFrame(frame);
    }
    if (!focusedResultId && !focusedWorkTicket) {
      setActiveKey('0');
    }
    return undefined;
  }, [data, focusedResultId, focusedWorkTicket]);

  // Stop pagination loading when new data arrives
  useEffect(() => {
    if (data && !isLoading) {
      setIsPaginationLoading(false);
    }
  }, [data, isLoading]);

  const handleEditClick = (e, record) => {
    e.stopPropagation();
    dispatch(fetchInspectionResultsData({ 
      inspectionresults: record, 
      pageNumber: 1, 
      pageSize: 10 
    }));
    dispatch(setSelectedPart(record));
  };

  const handleDeleteClick = id => {
    // dispatch(deleteinspectionResultss(id));
  };

  const handleCheckboxChange = (e, record) => {
    // Handle checkbox change logic here
    console.log('Checkbox changed', record, e.target.checked);
  };

  const handlePageChange = async (pageNumber) => {
    if (onPageChange) {
      setIsPaginationLoading(true);
      try {
        await onPageChange(pageNumber);
      } finally {
        // Loading will be stopped when new data arrives via useEffect
      }
    }
  };

  const handlePageSizeChange = async (pageSize) => {
    if (onPageChange) {
      setIsPaginationLoading(true);
      try {
        await onPageChange(1, pageSize); // Reset to first page when changing page size
      } finally {
        // Loading will be stopped when new data arrives via useEffect
      }
    }
  };

  const handleDateChange = e => {
    const newDate = e.target.value;
    const newDateName = e.target.name;
    let dateObj = {
      toDate,
      fromDate,
    };
    if (newDateName == 'toDate') {
      dateObj = {
        toDate: newDate,
        fromDate: fromDate,
      };
    } else {
      dateObj = {
        toDate: toDate,
        fromDate: newDate,
      };
    }

    setFromDate(dateObj.fromDate);
    setToDate(dateObj.toDate);
  };

  const handleFileDownload = (file) => {
    if (file.filePath) {
      // Create a download link
      const link = document.createElement('a');
      link.href = file.filePath;
      link.download = file.fileName || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('File path not available for download');
    }
  };

  // Handler for opening zoom modal
  const handleImageClick = (e, file) => {
    e.stopPropagation();
    setSelectedImage(file);
    setShowZoomModal(true);
  };

  // Handler for closing zoom modal
  const handleCloseZoomModal = () => {
    setShowZoomModal(false);
    setSelectedImage(null);
  };

  // Handler for download from zoom modal
  const handleZoomModalDownload = (imageSrc) => {
    downloadImage(imageSrc);
  };

  const stripHtmlTags = (html) => {
    if (!html) return 'N/A';
    return html.replace(/<[^>]*>/g, '').trim() || 'N/A';
  };

  const renderAccordionHeader = (aqlResult) => {
    return (
      <div className="d-flex justify-content-between align-items-center w-100">
        <div className="d-flex align-items-center gap-3">
          <span><strong>Work Ticket:</strong> {aqlResult.workTicketNumber || 'N/A'}</span>
          <div className="d-flex align-items-center gap-2 ms-3">
            <span className="text-success"><strong>Good:</strong> {aqlResult.good || '0'}</span>
            <span className="text-warning"><strong>Minor:</strong> {aqlResult.minor || '0'}</span>
            <span className="text-danger"><strong>Major:</strong> {aqlResult.major || '0'}</span>
            <span className="text-danger fw-bold"><strong>Critical:</strong> {aqlResult.critical || '0'}</span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className={`badge ${aqlResult.status === 'Passed' ? 'bg-success' : 'bg-danger'}`}>
            {aqlResult.status || 'N/A'}
          </span>
          <span className="text-muted">
            {aqlResult.inspectionDate ? new Date(aqlResult.inspectionDate).toLocaleDateString() : ''}
          </span>
        </div>
      </div>
    );
  };

  const renderInspectionResults = (inspectionResults) => {
    if (!inspectionResults || inspectionResults.length === 0) {
      return <p className="text-muted">No inspection results available</p>;
    }

    return (
      <Table striped bordered hover size="sm" className="mb-3">
        <thead>
          <tr>
            <th>Step</th>
            <th>Inspection Plan</th>
            <th>Quality Status</th>
          </tr>
        </thead>
        <tbody>
          {inspectionResults.map((result, index) => (
            <tr key={index}>
              <td>{result.step || result.stepNumber || result.stepName || 'N/A'}</td>
              <td>{stripHtmlTags(result.inspectionPlan || result.parameterName)}</td>
              <td>
                <span className={`badge ${result.qualityStatus === 'Y' ? 'bg-success' : 'bg-danger'}`}>
                  {result.qualityStatus === 'Y' ? 'Yes' : 'No'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const renderParameters = (parameters) => {
    if (!parameters || parameters.length === 0) {
      return <p className="text-muted">No parameters available</p>;
    }

    // Group parameters by question
    const groupedByQuestion = {};
    parameters.forEach((param) => {
      const question = param.question || 'N/A';
      if (!groupedByQuestion[question]) {
        groupedByQuestion[question] = [];
      }
      groupedByQuestion[question].push(param);
    });

    // Find maximum number of responses across all questions
    const maxResponses = Math.max(
      ...Object.values(groupedByQuestion).map((params) => params.length),
      0
    );

    // Convert to array for rendering
    const questions = Object.keys(groupedByQuestion);

    return (
      <Table striped size="sm" className="mb-3" style={{ border: 'none' }}>
        <tbody>
          {questions.map((question, questionIndex) => {
            const questionParams = groupedByQuestion[question];
            return (
              <tr key={questionIndex} style={{ border: 'none' }}>
                {/* Serial Number */}
                <td style={{ border: 'none', padding: '8px' }}>
                  {questionIndex + 1}
                </td>
                {/* Question */}
                <td style={{ border: 'none', padding: '8px' }}>
                  {question}
                </td>
                {/* Dynamic Response columns */}
                {Array.from({ length: maxResponses }).map((_, responseIndex) => {
                  const param = questionParams[responseIndex];
                  const cellValue = param
                    ? `${param.response || 'N/A'} : ${param.answer || 'N/A'}(${param.expectedValue || 'N/A'})`
                    : '';
                  return (
                    <td
                      key={responseIndex}
                      style={{ border: 'none', padding: '8px' }}
                    >
                      {cellValue}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const renderInspectionFiles = (inspectionFiles) => {
    if (!inspectionFiles || inspectionFiles.length === 0) {
      return <p className="text-muted">No files available</p>;
    }

    return (
      <div className="d-flex flex-wrap gap-2">
        {inspectionFiles.map((file, index) => (
          <div key={index} className="file-item d-flex align-items-center position-relative" style={accordionStyles.fileItem}>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip style={{ display: 'none' }}>
                  {file.fileName || 'Download File'}
                </Tooltip>
              }
              trigger={[]}
            >
              <div
                className="file-tile position-relative"
                onMouseEnter={(e) => {
                  const hoverBox = e.currentTarget.querySelector('.hover-image-box');
                  if (hoverBox) hoverBox.style.display = 'block';
                }}
                onMouseLeave={(e) => {
                  const hoverBox = e.currentTarget.querySelector('.hover-image-box');
                  if (hoverBox) hoverBox.style.display = 'none';
                }}
              >
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="d-flex align-items-center gap-1 me-2"
                  onClick={() => handleFileDownload(file)}
                  style={{ border: 'none', backgroundColor: 'transparent', color: '#007bff' }}
                >
                  <FileText size={16} />
                  <Download size={12} />
                </Button>
                
                {/* Hover Image Box */}
                <div 
                  className="hover-image-box position-absolute bg-white border rounded shadow-lg p-2"
                  style={{
                    display: 'none',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1050,
                    minWidth: '200px',
                    maxWidth: '300px',
                    marginBottom: '5px'
                  }}
                >
                  <div className="text-center">
                    <img 
                      src={file.filePath || "/api/placeholder/250/150"} 
                      alt={file.fileName}
                      className="img-fluid rounded"
                      style={{ 
                        maxWidth: '250px', 
                        maxHeight: '150px', 
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => handleImageClick(e, file)}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="d-flex align-items-center justify-content-center"
                      style={{ 
                        display: 'none',
                        height: '100px',
                        color: '#6c757d'
                      }}
                    >
                      <FileText size={48} />
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">{file.fileName || 'Unknown File'}</small>
                    </div>
                  </div>
                </div>
              </div>
            </OverlayTrigger>
            
            {file.fileName && (
              <span className="small text-dark">{file.fileName}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const searchByDateRange = () => {
    const date = new Date();
    return (
      <Card className="borderCard p-3">
        <div className="col-12">
          <div className="row">
            <div className="col-12 col-lg-3 mb-4 mb-lg-0">
              <Form.Control
                type="date"
                name="fromDate"
                placeholder="DateRange"
                value={fromDate}
                onChange={e => handleDateChange(e)}
              />
            </div>
            <div className="col-12 col-lg-3 mb-4 mb-lg-0">
              <Form.Control
                type="date"
                name="toDate"
                placeholder="DateRange"
                value={toDate}
                onChange={e => handleDateChange(e)}
              />
            </div>
            <div className="col-12 col-lg-3 mb-4 mb-lg-0">
              <Button
                style={{ backgroundColor: '#ff9f43' }}
                onClick={() => dispatch(setInspectionResultsDataFilters({ toDate, fromDate }))}
              >
                Search
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="table-responsive product-list">
      {(isLoading || isPaginationLoading) && <LoadingSpinner />}
      {searchByDateRange()}
      
      <div className="aql-results-accordion mt-3" style={accordionStyles.aqlResultsAccordion}>
        <Accordion activeKey={activeKey} onSelect={setActiveKey} className="custom-aql-accordion">
          {data && data.map((aqlResult, index) => {
            const isFocused = findFocusedAqlIndex([aqlResult], focusedResultId, focusedWorkTicket) === 0;
            return (
            <Accordion.Item
              eventKey={index.toString()}
              key={aqlResult.id ?? index}
              id={aqlResult.id != null ? `aql-result-${aqlResult.id}` : undefined}
              className="mb-2"
              style={isFocused ? { boxShadow: '0 0 0 2px #1976d2', borderRadius: 6 } : undefined}
            >
              <Accordion.Header style={{ backgroundColor: '#f8f9fa' }}>
                {renderAccordionHeader(aqlResult)}
              </Accordion.Header>
              <Accordion.Body style={{ backgroundColor: '#fafafa', padding: '1.5rem' }}>
                <div className="aql-details">
                  {/* Master Data Details */}
                  <div className="mb-4">
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-2"><strong>Part Weight:</strong> <span className="text-muted">{aqlResult.partWeight || 'N/A'}</span></p>
                        <p className="mb-2"><strong>Cycle Time:</strong> <span className="text-muted">{aqlResult.cycleTime || 'N/A'}</span></p>
                        <p className="mb-2"><strong>Material Batch Number:</strong> <span className="text-muted">{aqlResult.materialBatchNumber || 'N/A'}</span></p>
                        <p className="mb-2"><strong>Inspector:</strong> <span className="text-muted">{aqlResult.inspectorName || 'N/A'}</span></p>
                        <p className="mb-2"><strong>Good:</strong> <span className="text-success">{aqlResult.good || '0'}</span></p>
                        <p className="mb-2"><strong>Minor:</strong> <span className="text-warning">{aqlResult.minor || '0'}</span></p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-2"><strong>Work Ticket:</strong> <span className="text-muted">{aqlResult.workTicketNumber || 'N/A'}</span></p>
                        <p className="mb-2"><strong>Status:</strong> 
                          <span className={`badge ms-2 ${aqlResult.status === 'Passed' ? 'bg-success' : 'bg-danger'}`}>
                            {aqlResult.status || 'N/A'}
                          </span>
                        </p>
                        <p className="mb-2"><strong>Inspection Date:</strong> <span className="text-muted">{aqlResult.inspectionDate ? new Date(aqlResult.inspectionDate).toLocaleString() : 'N/A'}</span></p>
                        <p className="mb-2"><strong>Comments:</strong> <span className="text-muted">{aqlResult.comments || 'No comments'}</span></p>
                        <p className="mb-2"><strong>Major:</strong> <span className="text-danger">{aqlResult.major || '0'}</span></p>
                        <p className="mb-2"><strong>Critical:</strong> <span className="text-danger fw-bold">{aqlResult.critical || '0'}</span></p>
                      </div>
                    </div>
                    {aqlResult.exceptionResult && (
                      <div className="alert alert-warning mt-3">
                        <strong>Exception:</strong> {aqlResult.exceptionResult}
                      </div>
                    )}
                  </div>

                  {/* Inspection Results Section */}
                  <div className="mb-4">
                    <h6 className="text-primary mb-3 border-bottom pb-2">
                      <FileText size={18} className="me-2" />
                      Inspection Results
                    </h6>
                    {renderInspectionResults(aqlResult.inspectionResults)}
                  </div>

                  {/* Parameters Section */}
                  <div className="mb-4">
                    <h6 className="text-primary mb-3 border-bottom pb-2">
                      <Info size={18} className="me-2" />
                      Parameters
                    </h6>
                    {renderParameters(aqlResult.parameters)}
                  </div>

                  {/* Inspection Files Section */}
                  <div className="mb-3">
                    <h6 className="text-primary mb-3 border-bottom pb-2">
                      <Download size={18} className="me-2" />
                      Inspection Files
                    </h6>
                    {renderInspectionFiles(aqlResult.inspectionResultFiles)}
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
            );
          })}
        </Accordion>

        {(!data || data.length === 0) && !isLoading && (
          <div className="text-center py-4">
            <p className="text-muted">No AQL results found for the selected criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.totalCount > 0 && pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4 p-3 bg-light rounded position-relative">
          <div className="d-flex align-items-center gap-3">
            <span className="text-muted">
              Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of{' '}
              {pagination.totalCount} results
            </span>
            <Form.Select
              size="sm"
              style={{ width: '80px' }}
              value={pagination.pageSize}
              disabled={isPaginationLoading}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Form.Select>
            <span className="text-muted">per page</span>
          </div>

          <Pagination size="sm">
            <Pagination.First 
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage === 1 || isPaginationLoading}
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1 || isPaginationLoading}
            />
            
            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNum;
              if (pagination.totalPages <= 5) {
                pageNum = i + 1;
              } else if (pagination.currentPage <= 3) {
                pageNum = i + 1;
              } else if (pagination.currentPage >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i;
              } else {
                pageNum = pagination.currentPage - 2 + i;
              }

              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === pagination.currentPage}
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isPaginationLoading}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}
            
            <Pagination.Next 
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages || isPaginationLoading}
            />
            <Pagination.Last 
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages || isPaginationLoading}
            />
          </Pagination>
        </div>
      )}

      {/* Zoomable Image Modal */}
      <ZoomableImageModal
        show={showZoomModal}
        onHide={handleCloseZoomModal}
        imageSrc={selectedImage?.filePath}
        imageAlt={selectedImage?.fileName || 'Inspection File'}
        onDownload={handleZoomModalDownload}
      />
    </div>
  );
};

export default InspectionAQLResults;
